import { useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { FaRegImage } from "react-icons/fa6";
import { type Card } from "@memento/types/card";
import {
  FileUploadButton,
  type OnUploadPhoto,
} from "src/components/Renderer/FileUploadButton";
import config from "src/config";
import { useCreatePhotoSegment } from "src/hooks/useCreatePhotoSegment";
import { LoadingStatus } from "src/libs/fetcher";
import { defaultPhotoClipDurationSeconds } from "src/types/renderer";
import type {
  ButtonSize,
  ButtonWidth,
  ButtonVariant,
} from "@memento/ui/Button";
import { toastError } from "src/libs/toast-lib";
import { useSendAnalyticsEvent } from "src/libs/analytics-lib";

type AddPhotoButtonProps = {
  card: Card;
  uploadedClipSortOrder: number;
  uploaderType: "organizer" | "contributor";
  onAddSuccess?: () => void;
  onAddStart?: (selectedFile: string) => void;
  // called on failure or success
  onAddFinally?: () => void;
  disabled: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  width?: ButtonWidth;
  className?: string;
};

export const AddPhotoButton = ({
  card,
  uploadedClipSortOrder,
  uploaderType,
  onAddStart = () => {},
  onAddSuccess = () => {},
  onAddFinally = () => {},
  disabled,
  variant,
  size,
  width,
  className,
}: AddPhotoButtonProps) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [isCreatingSegment, setIsCreatingSegment] = useState(false);

  const useCreatePhotoSegmentRequest = useCreatePhotoSegment({
    onSuccess: () => {
      onAddSuccess();
      sendAnalyticsEvent({
        event: "PhotoClipSubmitted",
        data: {
          cardId: card.cardId,
          contributorId: undefined,
          uploaderType,
        },
      });
      onAddFinally();
      setIsCreatingSegment(false);
    },
    onFailure: (error) => {
      onAddFinally();
      toastError(
        "Something went wrong and your photo could not be uploaded. Please try again."
      );
      setIsCreatingSegment(false);
      Sentry.captureException(
        new Error(
          "Error while uploading photo segment in AddPhotoButton component"
        ),
        {
          extra: {
            errorMessage: error,
            cardDetails: card,
          },
        }
      );
    },
  });

  function handleUploadStart(selectedFile: File) {
    const imageUrl = URL.createObjectURL(selectedFile);
    onAddStart(imageUrl);
  }

  const handlePhotoUpload: OnUploadPhoto = ({ url }) => {
    setIsCreatingSegment(true);

    useCreatePhotoSegmentRequest.trigger(
      { cardId: card.cardId },
      {
        url,
        sortOrder: uploadedClipSortOrder,
        durationSeconds:
          card.defaultPhotoClipDurationSeconds ??
          defaultPhotoClipDurationSeconds,
      }
    );
  };

  const buttonDisabled =
    useCreatePhotoSegmentRequest.status === LoadingStatus.LOADING || disabled;

  return (
    <FileUploadButton
      variant={variant}
      size={size}
      width={width}
      className={className}
      fileType="image"
      isLoading={isCreatingSegment}
      onUploadStart={handleUploadStart}
      onUploadSuccess={handlePhotoUpload}
      disabled={buttonDisabled}
      IconComponent={FaRegImage}
      text="Add Photo"
      maxUploadSizeMb={config.compilation.photoClip.maxUploadSizeMb}
    />
  );
};
